import { useMemo } from 'react'
import { AccountMembershipsQuery } from 'generated/resources/graphql'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { MembershipAction } from './MembershipActionsMenu'

type Membership = AccountMembershipsQuery['banking']['accountMemberships'][0]

interface UseMembershipActionsParams {
  membership: Membership
  currentEmployeeId: string
  onboardingAuthorEmployeeId?: string
}

export const useMembershipActions = ({
  membership,
  currentEmployeeId,
  onboardingAuthorEmployeeId,
}: UseMembershipActionsParams): MembershipAction[] => {
  const { navigation } = useNavigation()

  return useMemo(() => {
    const actions: MembershipAction[] = []

    const isOwner = membership.employee.id === onboardingAuthorEmployeeId
    if (isOwner) return actions

    const isCurrentUserOwner = currentEmployeeId === onboardingAuthorEmployeeId
    const isCurrentUserTheInvitee = membership.employee.id === currentEmployeeId
    const hasConsentUrl = !!membership.consent?.consentUrl
    const isPendingConsent = membership.status === 'pending_consent'
    const isInvitationSent = membership.status === 'invitation_sent'
    const isDisabled = membership.status === 'disabled'

    // Owner actions
    if (isCurrentUserOwner && isPendingConsent && hasConsentUrl) {
      actions.push({
        type: 'complete_consent',
        consentUrl: membership.consent?.consentUrl ?? '',
      })
    }

    if (isCurrentUserOwner && isInvitationSent) {
      actions.push({
        type: 'resend_invitation',
        membershipId: membership.id,
      })
    }

    if (isCurrentUserOwner && isDisabled) {
      actions.push({
        type: 'retry',
        membershipId: membership.id,
      })
    }

    // Invitee action: complete setup when invitation_sent
    if (isCurrentUserTheInvitee && isInvitationSent) {
      const accountsPath = navigation.finance.banking.accounts.fullPath()
      const setupUrl = `${accountsPath}?setup_membership=${membership.id}`

      actions.push({
        type: 'complete_setup',
        setupUrl,
      })
    }

    return actions
  }, [membership, currentEmployeeId, onboardingAuthorEmployeeId, navigation])
}
